/* ST 2026-10-18 subcategory rows on phones, variant U1 (Wrike 4583400208, Argus for Ali 18.10.2026).
   One source with the phone menu: views, chip labels, chip order and products all come from window.stMobileMenu
   (assets/st-mobile-menu.js: st_menu.views.<handle>.chips, custom.menu_chips / st_menu_v2_preview_chips), so a
   change in the menu changes these rows too. Nothing here is a list of its own.
   - Main chip -> menu view: data-st-view (snippets/st-category-chips.liquid), else the menu view that links to the
     chip's collection. A view whose menu shows chips gives the main chip an arrow and a row below the main row:
     "Alle" + the menu's chips. Below 990 px a tap on the main chip opens/closes the row (one open at a time).
   - A category page without a main row that is a menu view itself (Rudergeräte, Vibrationsplatten, Smith Machines)
     gets the view's chips as its row, phones only.
   - Chip targets: <view collection>?st_view=<view>&st_chip=<chip> ("Alle": without st_chip). On such a page the grid
     shows exactly the products of that chip in the menu, in the menu's order (cards of further pages are fetched
     through the Section Rendering API); the main chip of the view is active and its row open. Filter/sort results
     replace the grid (assets/collection.js) and are narrowed to the chip again (sort = the shop's order).
   Without JS or without the phone menu the chips are links.

   ST 2026-10-19 chips filter in place on phones (Argus for Ali 19.10.2026): a tap on a chip link no longer reloads
   the page. The target page is fetched (cached per URL), the template sections whose HTML differs are swapped (always
   main-collection: chips, filters, sort, count, grid, pagination; also banner/FAQ of the target collection), title,
   description, canonical and hreflang are updated, the URL is pushed (Back restores the previous chip through
   popstate) and the chip bar keeps its place on screen. Chips stay real links: without JS, with a modifier key, from
   990 px (desktop unchanged) or when the target uses another template (e.g. Sale), the browser follows the link.

   ST 2026-10-25 rows on desktop too (Wrike 4591276398, Ali 25.10.2026: "desktop exactly like mobile"): arrows, rows,
   the Rudergeräte row and in-place switching now work on every width. From 990 px the row sits on its own line under
   the F2 tabs (assets/st-category-chips.css); the page switch itself is still done by assets/st-chips-nav.js. */
(() => {
  if (window.stCategoryChips) return;
  window.stCategoryChips = true;

  // Chip view of the shown page (?st_view=…&st_chip=…), read again by start() after every in-place swap.
  let pView = null;
  let pChip = "all";
  let pageChip = null; // main chip of the chip view (?st_view=…)
  const CHEV =
    '<svg class="st-chip__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 9l6 6 6-6"/></svg>';

  const esc = (value) =>
    String(value == null ? "" : value).replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    );
  const pathOf = (url) =>
    new URL(url, window.location.origin).pathname.replace(/\/$/, "");
  const handleOf = (url) => {
    const part = pathOf(url).split("/products/")[1];
    return part ? part.split("/")[0] : "";
  };
  const chipUrl = (view, key, chip) => {
    const u = new URL(view.url, window.location.origin);
    u.searchParams.set("st_view", key);
    if (chip !== "all") u.searchParams.set("st_chip", chip);
    return u.pathname + u.search;
  };

  // Server state of the main row (active chip), so closing a row or leaving the chip view restores it.
  const remember = (nav) => {
    nav.querySelectorAll(".st-chip").forEach((chip) => {
      chip.dataset.stActive = chip.classList.contains("is-active") ? "1" : "";
      chip.dataset.stCurrent = chip.getAttribute("aria-current") || "";
    });
  };
  const markMain = (nav, active) => {
    nav.querySelectorAll(".st-chip").forEach((chip) => {
      const on = active ? chip === active : chip.dataset.stActive === "1";
      chip.classList.toggle("is-active", on);
      const current = active
        ? on
          ? chip.dataset.stCurrent || "true"
          : ""
        : chip.dataset.stCurrent;
      if (current) chip.setAttribute("aria-current", current);
      else chip.removeAttribute("aria-current");
    });
  };

  // Like the U1 mock: an open row without an active subcategory shows its "Alle" chip filled (visual only).
  const setRow = (chip, open) => {
    const row = document.getElementById(chip.dataset.stSub);
    if (row) {
      row.hidden = !open;
      const all = row.querySelector(".st-subchip[data-st-all]");
      if (all && !all.hasAttribute("aria-current"))
        all.classList.toggle(
          "is-active",
          open && !row.querySelector(".st-subchip[aria-current]"),
        );
    }
    chip.setAttribute("aria-expanded", open ? "true" : "false");
  };

  // Arrows can make a short row overflow the phone width: then it becomes the scroll row of the design.
  const fit = (nav) => {
    if (
      !nav.classList.contains("st-chips--grid") &&
      !nav.classList.contains("st-chips--seg") &&
      nav.scrollWidth > nav.clientWidth + 1
    )
      nav.classList.add("st-chips--scroll");
  };

  const rowHtml = (id, label, view, key, data, active, cls) =>
    '<nav class="' +
    cls +
    '" id="' +
    id +
    '" aria-label="' +
    esc(label) +
    '"' +
    (cls === "st-chips-sub" ? " hidden" : "") +
    ">" +
    [["all", window.stMobileMenu.all]]
      .concat(data.chips)
      .map((c) => {
        const on = active === c[0];
        return (
          '<a class="' +
          (cls === "st-chips-sub" ? "st-subchip" : "st-chip") +
          (on ? " is-active" : "") +
          '" href="' +
          esc(chipUrl(view, key, c[0])) +
          '"' +
          (c[0] === "all" ? " data-st-all" : "") +
          (on ? ' aria-current="page"' : "") +
          "><span>" +
          esc(c[1]) +
          "</span></a>"
        );
      })
      .join("") +
    "</nav>";

  // Chip view: the grid shows the products of the chip, in the menu's order.
  const cardsOf = (root, cards) => {
    root.querySelectorAll(".product-card").forEach((card) => {
      const link = card.querySelector('a[href*="/products/"]');
      const handle = link && handleOf(link.getAttribute("href"));
      if (handle && !cards[handle]) cards[handle] = card;
    });
    return cards;
  };

  const filterGrid = (list) => {
    const container = document.getElementById("ProductGridContainer");
    const grid = container && container.querySelector(".card-grid");
    const section = grid && grid.closest(".shopify-section");
    if (!grid || !section) return Promise.resolve();
    const handles = list.map((p) => handleOf(p.u)).filter(Boolean);
    const cards = cardsOf(grid, {});
    const sectionId = section.id.replace("shopify-section-", "");
    const more = (page) => {
      if (page > 10 || handles.every((h) => cards[h])) return Promise.resolve();
      // Same sort and filters as the page, so further pages hold the right cards in the right order.
      const u = new URL(window.location.href);
      u.searchParams.delete("st_view");
      u.searchParams.delete("st_chip");
      u.hash = "";
      u.searchParams.set("section_id", sectionId);
      u.searchParams.set("page", page);
      return fetch(u.toString(), { credentials: "same-origin" })
        .then((res) => (res.ok ? res.text() : ""))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const before = Object.keys(cards).length;
          cardsOf(doc, cards);
          if (Object.keys(cards).length > before) return more(page + 1);
        });
    };
    return more(2)
      .catch(() => {})
      .then(() => {
        // Menu order, unless the shopper picked a sort: then the shop's order (cards were collected page by page).
        const wanted = new Set(handles);
        const shown = new URLSearchParams(window.location.search).get("sort_by")
          ? Object.keys(cards).filter((h) => wanted.has(h))
          : handles.filter((h) => cards[h]);
        if (!shown.length) return;
        grid.replaceChildren(
          ...shown.map((h) =>
            cards[h].ownerDocument === document
              ? cards[h]
              : document.importNode(cards[h], true),
          ),
        );
        container
          .querySelectorAll(".pagination")
          .forEach((el) => (el.hidden = true));
        // "Show N results" in the filter drawer counts the chip's products.
        document
          .querySelectorAll("results-count")
          .forEach((el) => (el.textContent = shown.length));
      });
  };

  const start = () => {
    const params = new URLSearchParams(window.location.search);
    pView = params.get("st_view");
    pChip = params.get("st_chip") || "all";
    pageChip = null;
    const api = window.stMobileMenu;
    if (!api || !api.views) return Promise.resolve();
    const views = api.views;
    const keys = Object.keys(views);
    const withChips = (key) =>
      views[key] && String(views[key].chips || "").trim();
    const nav = document.querySelector(".st-chips");
    const pairs = [];
    const state = history.state;
    const saved =
      state && typeof state.stOpen === "string" ? state.stOpen : null;

    if (nav) {
      remember(nav);
      const used = {};
      nav.querySelectorAll(".st-chip[data-st-view]").forEach((chip) => {
        const key = chip.dataset.stView;
        if (views[key]) {
          pairs.push([chip, key]);
          used[key] = true;
        }
      });
      nav
        .querySelectorAll(".st-chip[data-st-coll]:not([data-st-view])")
        .forEach((chip) => {
          const path = pathOf(chip.getAttribute("href"));
          const key = keys.find(
            (k) => !used[k] && pathOf(views[k].url) === path,
          );
          if (key) {
            pairs.push([chip, key]);
            used[key] = true;
          }
        });
    }

    pairs.forEach(([chip, key]) => {
      if (key === pView) pageChip = chip;
    });
    if (pageChip) markMain(nav, pageChip);
    // The Sale segment bar (st-chips--seg, round 10 V3) is plain text segments: no arrows, no rows.
    const rows =
      nav && nav.classList.contains("st-chips--seg")
        ? []
        : pairs.filter(([, key]) => withChips(key));

    // Main chips whose view has chips get their arrow at once (no wait for the product data).
    rows.forEach(([chip, key]) => {
      chip.insertAdjacentHTML("beforeend", CHEV);
      chip.dataset.stSub = "st-sub-" + key;
      chip.setAttribute("aria-controls", chip.dataset.stSub);
      chip.setAttribute("aria-expanded", "false");
    });
    if (nav) fit(nav);

    const jobs = rows.map(([chip, key]) =>
      api
        .data(key)
        .then((data) => {
          if (!data.chips.length) {
            const arrow = chip.querySelector(".st-chip__chev");
            if (arrow) arrow.remove();
            delete chip.dataset.stSub;
            chip.removeAttribute("aria-controls");
            chip.removeAttribute("aria-expanded");
            return;
          }
          const label = chip.textContent.trim();
          const active = pView === key ? pChip : "";
          const subs = document.querySelectorAll(".st-chips-sub");
          (subs.length ? subs[subs.length - 1] : nav).insertAdjacentHTML(
            "afterend",
            rowHtml(
              chip.dataset.stSub,
              label,
              data.view,
              key,
              data,
              active,
              "st-chips-sub",
            ),
          );
          // A row the shopper opened or closed on this history entry comes back so on Back/Forward (see the toggle).
          const open =
            saved !== null
              ? saved === key
              : pView === key || (!pView && chip.dataset.stActive === "1");
          setRow(chip, open);
          if (open && saved === key) markMain(nav, chip);
          return [key, data];
        })
        .catch(() => {}),
    );

    // A category page without a main row that is a menu view with chips itself (e.g. Rudergeräte).
    if (!nav) {
      const here = pathOf(window.location.href);
      const key = keys.find(
        (k) => withChips(k) && pathOf(views[k].url) === here,
      );
      const topbar = document.querySelector(
        ".collection__topbar .facet-topbar",
      );
      if (key && topbar) {
        jobs.push(
          api
            .data(key)
            .then((data) => {
              if (!data.chips.length) return;
              const slot = document.createElement("div");
              slot.className = "st-chips-slot st-chips-slot--menu";
              slot.innerHTML = rowHtml(
                "st-row-" + key,
                views[key].title,
                data.view,
                key,
                data,
                pView === key ? pChip : "all",
                "st-chips st-chips--menu",
              );
              topbar.prepend(slot);
              fit(slot.firstElementChild);
              return [key, data];
            })
            .catch(() => {}),
        );
      }
    }

    // Chip view (?st_view=…): narrow the grid to the products the menu shows for this chip.
    if (pView && views[pView]) {
      const apply = () =>
        api
          .data(pView)
          .then((data) => {
            const chip = data.chips.some((c) => c[0] === pChip) ? pChip : "all";
            return filterGrid(api.products(pView, data.list, chip));
          })
          .catch(() => {});
      jobs.push(apply());
      const container = document.getElementById("ProductGridContainer");
      if (container) {
        // Filter/sort (assets/collection.js) replace the grid: the chip stays, its products are narrowed again
        // (filter AND chip). The filter form drops st_view/st_chip from the URL, so they are put back.
        const view = pView;
        const chip = pChip;
        new MutationObserver((records) => {
          if (!records.some((r) => r.target === container)) return;
          const u = new URL(window.location.href);
          if (u.searchParams.get("st_view") !== view) {
            u.searchParams.set("st_view", view);
            if (chip !== "all") u.searchParams.set("st_chip", chip);
            history.replaceState(history.state, "", u.toString());
          }
          apply();
        }).observe(container, { childList: true });
      }
    }
    return Promise.all(jobs);
  };

  document.addEventListener("click", (event) => {
    const chip = event.target.closest(".st-chip[data-st-sub]");
    if (!chip || !document.getElementById(chip.dataset.stSub)) return;
    const nav = chip.closest(".st-chips");
    if (!nav) return;
    event.preventDefault();
    const open = chip.getAttribute("aria-expanded") !== "true";
    nav.querySelectorAll(".st-chip[data-st-sub]").forEach((other) => {
      if (other !== chip) setRow(other, false);
    });
    setRow(chip, open);
    markMain(nav, open ? chip : pageChip);
    // Opening a row is no history step, but the entry remembers it, so Back to this page reopens it (start()).
    // The page switches keep the entry's state (Object.assign in go() and in assets/st-chips-nav.js).
    history.replaceState(
      Object.assign({}, history.state, {
        stOpen: open ? chip.dataset.stSub.replace(/^st-sub-/, "") : "",
      }),
      "",
      window.location.href,
    );
  });

  // In-place chip navigation. Pages are cached as promises of their HTML; st_view/st_chip only change what
  // the JS shows, so all chips of one collection share one fetch.
  const pages = new Map();
  let navigated = false;
  let pending = null;
  const pageKey = (url) => {
    const u = new URL(url, window.location.origin);
    u.searchParams.delete("st_view");
    u.searchParams.delete("st_chip");
    u.hash = "";
    return u.toString();
  };
  const fetchPage = (url) => {
    const key = pageKey(url);
    if (!pages.has(key)) {
      pages.set(
        key,
        fetch(key, { credentials: "same-origin" }).then((res) => {
          if (!res.ok || res.redirected) throw new Error("page");
          return res.text();
        }),
      );
      pages.get(key).catch(() => pages.delete(key));
      if (pages.size > 20) pages.delete(pages.keys().next().value);
    }
    return pages.get(key);
  };
  const sectionsOf = (root) =>
    Array.from(root.querySelectorAll("#MainContent > .shopify-section"));

  // Scripts that come with swapped markup: inline ones run again (they guard themselves), files only once.
  const runScripts = (root) => {
    root.querySelectorAll("script").forEach((old) => {
      const type = old.getAttribute("type");
      if (type && type !== "text/javascript" && type !== "module") return;
      const src = old.getAttribute("src");
      if (
        src &&
        Array.from(document.scripts).some(
          (s) => s !== old && s.getAttribute("src") === src,
        )
      )
        return;
      const script = document.createElement("script");
      Array.from(old.attributes).forEach((a) =>
        script.setAttribute(a.name, a.value),
      );
      script.textContent = old.textContent;
      old.replaceWith(script);
    });
  };

  const syncHead = (doc, url) => {
    document.title = doc.title;
    [
      'link[rel="canonical"]',
      'meta[name="description"]',
      'meta[property="og:url"]',
      'meta[property="og:title"]',
      'meta[property="og:description"]',
    ].forEach((sel) => {
      const now = document.head.querySelector(sel);
      const next = doc.head.querySelector(sel);
      if (now && next) now.replaceWith(next.cloneNode(true));
    });
    const alternates = doc.head.querySelectorAll(
      "link[rel=alternate][hreflang]",
    );
    if (alternates.length) {
      document.head
        .querySelectorAll("link[rel=alternate][hreflang]")
        .forEach((el) => el.remove());
      alternates.forEach((el) => document.head.append(el.cloneNode(true)));
    }
    // Country/language switch returns to the page that is shown now.
    const path = new URL(url, window.location.origin).pathname;
    document
      .querySelectorAll('form[action*="/localization"] input[name="return_to"]')
      .forEach((input) => (input.value = path));
  };

  const markTapped = (link) => {
    // Sub-type tabs (Wrike 4586486589, round 11) are single-select too.
    const row = link.closest(".st-chips, .st-chips-sub, .st-chips-types");
    if (!row) return;
    row.querySelectorAll(".st-chip, .st-subchip").forEach((chip) => {
      const on = chip === link;
      chip.classList.toggle("is-active", on);
      if (on) chip.setAttribute("aria-current", "page");
      else chip.removeAttribute("aria-current");
    });
  };

  const setBusy = (on) => {
    const grid = document.getElementById("ProductGridContainer");
    if (!grid) return;
    grid.classList.toggle("st-chips-loading", on);
    if (on) grid.setAttribute("aria-busy", "true");
    else grid.removeAttribute("aria-busy");
  };

  const go = (url, push) => {
    // Adapter (Wrike 4584025633 round 5): with assets/st-chips-nav.js (PR #217) on the page, the page switch is done
    // there (one Section Rendering request, hero as text, chip row anchored), so every chip tap ends the same way;
    // start() runs again on its st:chips:navigate event (below).
    if (window.stChipsNav && window.stChipsNav.go)
      return window.stChipsNav.go(url, push);
    const job = {};
    pending = job;
    setBusy(true);
    return fetchPage(url)
      .then((html) => {
        if (pending !== job) return;
        const doc = new DOMParser().parseFromString(html, "text/html");
        const gridSection = (root) => {
          const grid = root.getElementById("ProductGridContainer");
          return grid && grid.closest(".shopify-section");
        };
        const nowMain = gridSection(document);
        const nextMain = gridSection(doc);
        // Same template = same section id of the grid (template--<id>__main-collection); otherwise (e.g. Sale) a normal
        // page load.
        if (!nowMain || !nextMain || nowMain.id !== nextMain.id)
          throw new Error("template");

        const bar = document.querySelector("#MainContent .collection__topbar");
        const barTop = bar ? bar.getBoundingClientRect().top : null;
        const oldRow = document.querySelector("#MainContent .st-chips");
        const rowLeft = oldRow ? oldRow.scrollLeft : 0;

        if (push) {
          // The entry we leave keeps its scroll position for Back (restored below, not by the browser).
          history.replaceState(
            Object.assign({}, history.state, {
              stChips: true,
              stY: window.scrollY,
            }),
            "",
            window.location.href,
          );
          history.pushState({ stChips: true }, "", url);
        }
        if (!navigated && "scrollRestoration" in history)
          history.scrollRestoration = "manual";
        navigated = true;

        // Matched by id: sections that removed themselves on this page (e.g. an empty "recently viewed") stay away.
        sectionsOf(doc).forEach((fresh) => {
          const el = document.getElementById(fresh.id);
          if (!el || (el !== nowMain && el.innerHTML === fresh.innerHTML))
            return;
          el.className = fresh.className;
          el.innerHTML = fresh.innerHTML;
          runScripts(el);
        });
        syncHead(doc, url);

        // Keep the chip bar where it was on screen (no jump to the top, also when the banner above changes height);
        // Back/Forward return to the position the entry was left at.
        const savedY = !push && history.state && history.state.stY;
        const anchor = () => {
          const top = document.querySelector(
            "#MainContent .collection__topbar",
          );
          if (typeof savedY === "number") window.scrollTo(0, savedY);
          else if (top && barTop !== null)
            window.scrollBy(0, top.getBoundingClientRect().top - barTop);
        };
        anchor();
        const row = document.querySelector("#MainContent .st-chips");
        if (row) {
          row.scrollLeft = rowLeft;
          const active = row.querySelector(".st-chip.is-active");
          if (active) {
            const a = active.getBoundingClientRect();
            const r = row.getBoundingClientRect();
            if (a.left < r.left || a.right > r.right)
              row.scrollLeft += a.left - r.left - 16;
          }
        }

        setBusy(true);
        document.dispatchEvent(new CustomEvent("collection:reloaded"));
        // The rows built by start() can shift the bar once more (e.g. the Rudergeräte row): anchor again.
        return Promise.resolve(window.stMobileMenu ? start() : null).then(
          () => {
            if (pending !== job) return;
            setBusy(false);
            // After the next layout (sections above with content-visibility settle one frame later).
            requestAnimationFrame(() => requestAnimationFrame(anchor));
          },
        );
      })
      .catch(() => {
        if (pending !== job) return;
        window.location.assign(url);
      });
  };

  const chipLink = (event) => {
    const link =
      event.target.closest &&
      event.target.closest(
        "a.st-chip[href], a.st-subchip[href], a.st-typechip[href]",
      );
    if (!link || !link.closest("#MainContent")) return null;
    const url = new URL(link.href, window.location.origin);
    if (
      url.origin !== window.location.origin ||
      !url.pathname.includes("/collections/") ||
      (link.target && link.target !== "_self") ||
      link.hasAttribute("download")
    )
      return null;
    return link;
  };

  // Warm the cache while the finger is still on the chip.
  document.addEventListener(
    "touchstart",
    (event) => {
      const link = chipLink(event);
      // assets/st-chips-nav.js warms its own cache (sections only) when it is on the page.
      if (link && !link.dataset.stSub && !window.stChipsNav)
        fetchPage(link.href).catch(() => {});
    },
    { passive: true },
  );

  // Registered after the row toggle above, so a main chip that opens its row (defaultPrevented) is left alone.
  document.addEventListener("click", (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    const link = chipLink(event);
    if (!link) return;
    event.preventDefault();
    if (link.href === window.location.href) return;
    markTapped(link);
    go(link.href, true);
  });

  window.addEventListener("popstate", () => {
    if (navigated) go(window.location.href, false);
  });

  // After a switch by assets/st-chips-nav.js: rebuild arrows and rows and narrow a chip view; that script keeps the
  // grid busy and the chip row anchored until this is done.
  document.addEventListener("st:chips:navigate", (event) => {
    const done = window.stMobileMenu ? start() : Promise.resolve();
    if (event.detail && event.detail.waitUntil) event.detail.waitUntil(done);
  });

  if (window.stMobileMenu) start();
  else document.addEventListener("st-mobile-menu:ready", start, { once: true });
})();
